import { createContext, useState, useContext, useEffect } from "react";

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem("cart");
    return stored ? JSON.parse(stored) : [];
  });

  // Save to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Helper – create unique key for each item (id + size)
  const makeKey = (product, size) => {
    return `${product.id || product.name}-${size || "NOSIZE"}`;
  };

  // Add to cart
  const addToCart = (product, size = null, quantity = 1) => {
    const key = makeKey(product, size);
    setCart((prev) => {
      const existing = prev.find((item) => item.key === key);
      if (existing) {
        // increase quantity if same item already exists
        return prev.map((item) =>
          item.key === key
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      // new entry
      return [...prev, { ...product, size, quantity, key }];
    });
  };

  // Update quantity
  const updateQuantity = (itemKey, quantity) => {
    setCart((prev) =>
      prev.map((item) =>
        item.key === itemKey
          ? { ...item, quantity: Math.max(1, quantity) }
          : item
      )
    );
  };

  // Remove from cart
  const removeFromCart = (itemKey) => {
    setCart((prev) => prev.filter((item) => item.key !== itemKey));
  };

  // Clear all
  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("cart");
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
